import React, { useState, useEffect, useRef } from "react";
import "./styles.css";

const VOWELS = ["a", "e", "i", "o", "u"];
const ALPHABET = "abcdefghijklmnopqrstuvwxyz".split("");
const DICTIONARY_URL =
  "https://raw.githubusercontent.com/dwyl/english-words/master/words_dictionary.json";

const generateValidStart = (dictSet) => {
  while (true) {
    const letters = Array.from(
      { length: 3 },
      () => ALPHABET[Math.floor(Math.random() * 26)]
    );

    if (
      !letters.some((l) => VOWELS.includes(l)) ||
      letters.every((l) => VOWELS.includes(l))
    )
      continue;

    for (let extra of ALPHABET) {
      const combo = [...letters, extra];
      const sortedCombo = combo.slice().sort().join("");
      for (let word of dictSet) {
        if (word.length !== 4) continue;
        const sortedWord = word.split("").sort().join("");
        if (sortedCombo === sortedWord) return letters;
      }
    }
  }
};

const isValid = (word, letters, dict) => {
  if (word.length !== letters.length + 1) return false;
  if (!dict.has(word)) return false;

  const wordFreq = {};
  const letterFreq = {};

  for (let c of word) wordFreq[c] = (wordFreq[c] || 0) + 1;
  for (let c of letters) letterFreq[c] = (letterFreq[c] || 0) + 1;

  let extra = 0;
  for (let c in wordFreq) {
    const diff = wordFreq[c] - (letterFreq[c] || 0);
    if (diff > 0) extra += diff;
    if (wordFreq[c] < (letterFreq[c] || 0)) return false;
  }

  return extra === 1;
};

const findValidWord = (letters, dict) => {
  for (let extra of ALPHABET) {
    const combo = [...letters, extra];
    for (let word of dict) {
      if (word.length === combo.length && isValid(word, letters, dict)) {
        return word;
      }
    }
  }
  return null;
};

export default function App() {
  const [dictionary, setDictionary] = useState(null);
  const [letters, setLetters] = useState([]);
  const [inputLetters, setInputLetters] = useState([]);
  const [message, setMessage] = useState("");
  const [startTime, setStartTime] = useState(Date.now());
  const [streak, setStreak] = useState(0);
  const [best, setBest] = useState(0);
  const firstInputRef = useRef(null);

  useEffect(() => {
    fetch(DICTIONARY_URL)
      .then((r) => r.json())
      .then((obj) => {
        const dictSet = new Set(Object.keys(obj));
        setDictionary(dictSet);
        const validStart = generateValidStart(dictSet);
        setLetters(validStart);
        setInputLetters(Array(validStart.length + 1).fill(""));
        setStartTime(Date.now());
        setTimeout(() => {
          if (firstInputRef.current) firstInputRef.current.focus();
        }, 0);
      });
  }, []);

  const handleSubmit = () => {
    const input = inputLetters.join("").toLowerCase().trim();

    if (!dictionary || !isValid(input, letters, dictionary)) {
      const suggestion = findValidWord(letters, dictionary);
      setMessage(
        suggestion
          ? `❌ Invalid. One possible word was: "${suggestion}"`
          : "❌ Invalid. No possible word found."
      );
      setBest(Math.max(best, streak));
      return;
    }

    const addedChar = [...input].find(
      (c) => input.split(c).length > letters.join("").split(c).length
    );

    const newLetters = [...letters, addedChar];

    setStreak(streak + 1);
    setBest(Math.max(best, streak + 1));
    setLetters(newLetters);
    setInputLetters(Array(newLetters.length + 1).fill(""));
    setStartTime(Date.now());
    setMessage("✅ Good job!");
    setTimeout(() => {
      if (firstInputRef.current) firstInputRef.current.focus();
    }, 0);
  };

  const restartGame = () => {
    if (!dictionary) return;
    const newStart = generateValidStart(dictionary);
    setLetters(newStart);
    setInputLetters(Array(newStart.length + 1).fill(""));
    setStreak(0);
    setMessage("");
    setStartTime(Date.now());
    setTimeout(() => {
      if (firstInputRef.current) firstInputRef.current.focus();
    }, 0);
  };

  return (
    <div className="app">
      <header>
        <button onClick={restartGame}>🔄 Restart</button>
      </header>
      <main>
        <h1>🧠 WordWorm Game</h1>
        <p className="instructions">
          Start with 3 letters. Add one letter to form a valid word. Letters may
          be rearranged. If correct, that word becomes the next round. Try to
          grow the word as long as you can!
        </p>
        <div className="letters">
          <strong>Current Letters:</strong> {letters.join(" ")}
        </div>
        <div className="letter-inputs">
          {inputLetters.map((char, idx) => (
            <input
              key={idx}
              type="text"
              maxLength={1}
              value={char}
              ref={idx === 0 ? firstInputRef : null}
              onChange={(e) => {
                const newInput = [...inputLetters];
                newInput[idx] = e.target.value.toLowerCase();
                setInputLetters(newInput);

                if (
                  e.target.value &&
                  idx < inputLetters.length - 1 &&
                  e.target.nextSibling
                ) {
                  e.target.nextSibling.focus();
                }
              }}
              onKeyDown={(e) => {
                if (
                  e.key === "Backspace" &&
                  !inputLetters[idx] &&
                  idx > 0 &&
                  e.target.previousSibling
                ) {
                  const newInput = [...inputLetters];
                  newInput[idx - 1] = "";
                  setInputLetters(newInput);
                  e.target.previousSibling.focus();
                }

                if (e.key === "Enter") {
                  handleSubmit();
                }
              }}
              className="letter-box"
            />
          ))}
        </div>
        <button onClick={handleSubmit}>Submit</button>
        <div className="message">{message}</div>
        <div className="scoreboard">
          <strong>Streak:</strong> {streak} | <strong>Best:</strong> {best}
        </div>
      </main>
    </div>
  );
}
